import React from "react";
import { PRICING, COMPANY } from "@/lib/site-data";
import { MATERIAL_ORDER, MATERIALS, materialSlug, SINGLE_MATERIAL_LOADS } from "@/lib/materials";
import { HOME } from "@/constants/testIds";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ArrowRight, Warning } from "@phosphor-icons/react";
import CleanLoadGuarantee from "@/components/site/CleanLoadGuarantee";
import NoSurprisesGuarantee from "@/components/site/NoSurprisesGuarantee";
import TextPhotoCta from "@/components/site/TextPhotoCta";

function bookUrl(material, size, tier_index) {
  const params = new URLSearchParams({ category: material, size, tier_index: String(tier_index) });
  return `/book?${params.toString()}`;
}

function PriceRow({ row, material }) {
  const is20yd = row.size === "20 yd³";
  return (
    <div className="border-b border-slate-200 last:border-b-0">
      <div className="grid grid-cols-1 sm:grid-cols-12 items-stretch">
        <div className="sm:col-span-3 px-5 py-5 bg-slate-50 border-r border-slate-200 flex items-center">
          <div>
            <div className="font-display font-black text-xl text-slate-900">{row.size}</div>
            <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-slate-500 mt-1">Bin size</div>
          </div>
        </div>
        <div className="sm:col-span-9 px-5 py-5 grid grid-cols-1 sm:grid-cols-3 gap-3">
          {row.tiers.map((t, i) => (
            <a
              key={i}
              href={bookUrl(material, row.size, i)}
              data-testid={`price-book-${materialSlug(material)}-${row.size.replace(/[^a-zA-Z0-9]/g, "")}-${i}`}
              className="group border-2 border-slate-900 px-4 py-3 flex items-center justify-between hover:bg-sky-50 hover:border-sky-500 transition-colors"
            >
              <div>
                <div className="text-[10px] font-bold tracking-[0.18em] uppercase text-slate-500">
                  {t.tons ? `Max ${t.tons} ton${t.tons > 1 ? "s" : ""}` : "Flat rate"}
                </div>
                <div className="font-display font-black text-2xl text-slate-900 mt-0.5">
                  ${t.price}
                  <span className="text-xs font-bold text-slate-500 ml-1">CAD</span>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                <span className="text-[9px] font-extrabold tracking-[0.18em] uppercase text-sky-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  Book
                </span>
                <ArrowRight size={18} weight="bold" className="text-slate-400 group-hover:text-sky-600 group-hover:translate-x-0.5 transition-all" />
              </div>
            </a>
          ))}
        </div>
      </div>
      {is20yd && (
        <div className="px-5 py-3 bg-amber-50 border-t border-amber-200 flex items-start gap-3">
          <Warning size={18} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
          <div className="text-xs text-slate-800 leading-relaxed">
            <span className="font-bold text-slate-900">Heavy materials in a 20 yd bin:</span>{" "}
            If your load includes soil, concrete, brick, or asphalt mixed in with your regular
            debris, keep it to about 4–5 wheelbarrow loads total. This keeps your bin safely
            haulable and helps you avoid overage charges.
          </div>
        </div>
      )}
    </div>
  );
}

function MaterialTab({ name, active }) {
  const meta = MATERIALS[name];
  return (
    <div className="flex items-center gap-2 sm:gap-3">
      {meta?.photo && (
        <div className={`h-8 w-8 sm:h-9 sm:w-9 border-2 overflow-hidden shrink-0 transition-colors ${
          active ? "border-white" : "border-slate-900"
        }`}>
          <img src={meta.photo} alt="" loading="lazy" className="h-full w-full object-cover" />
        </div>
      )}
      <span>{name}</span>
    </div>
  );
}

export default function PricingTabs() {
  return (
    <section data-testid={HOME.pricingSection} id="pricing" className="bg-white border-b-2 border-slate-200">
      <div className="mx-auto max-w-7xl px-6 lg:px-8 py-20 lg:py-28">
        <div className="flex items-center gap-3 mb-6">
          <div className="text-[11px] font-extrabold tracking-[0.22em] uppercase text-sky-600">
            Pricing &middot; Transparent flat rates
          </div>
        </div>

        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-12">
          <div>
            <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl text-slate-900 leading-tight max-w-3xl">
              Know your cost before you book.
            </h2>
            <p className="mt-4 max-w-3xl text-slate-600 leading-relaxed">
              Flat-rate mini-bin rentals with transparent pricing and no hidden surprises.
              Pick your material, pick your size, see the exact price. When you&apos;re ready,
              Book Now takes 90 seconds.
            </p>
            <div className="mt-6 grid sm:grid-cols-2 gap-5 max-w-3xl">
              <div>
                <div className="font-display font-bold text-slate-900 text-base">For Contractors</div>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                  Running jobs back-to-back? Know your cost before you book. Flat-rate pricing,
                  no hidden tonnage surprises, and bin sizes that match your job scope &mdash; from
                  a quick cleanout to a full demo.
                </p>
              </div>
              <div>
                <div className="font-display font-bold text-slate-900 text-base">For Homeowners &amp; One-Time Projects</div>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                  Cleaning out a garage, doing a move, or tackling a yard project? Same transparent
                  flat rates apply &mdash; pick your size, book online, done.
                </p>
              </div>
            </div>
          </div>
          <div className="flex flex-col gap-3 text-sm text-slate-600 max-w-sm">
            <div>
              <span className="font-bold text-slate-900">What&apos;s included:</span> 7-day rental,
              delivery &amp; pickup, standard tonnage per material. <span className="font-bold text-slate-900">HST added at checkout.</span>
            </div>
            <div>
              <span className="font-bold text-slate-900">Extras only if you go over:</span>{" "}
              Additional days $20/day. Overweight $0.15/kg on a certified scale (receipt provided).
            </div>
            <a
              href="/book"
              data-testid="pricing-book-cta"
              className="mt-2 inline-flex items-center justify-center gap-2 px-5 py-3 bg-sky-500 text-white font-bold text-xs uppercase tracking-[0.18em] border-2 border-sky-500 hover:bg-sky-600 hover:border-sky-600 transition-colors self-start"
            >
              Ready? Book Now →
            </a>
          </div>
        </div>

        <Tabs defaultValue={MATERIAL_ORDER[0]} className="w-full" data-testid={HOME.pricingTabs}>
          {/* Step 1 prompt — makes it obvious users have to pick a material first */}
          <div data-testid="pricing-step-1-prompt" className="border-2 border-sky-500 bg-sky-50 p-4 sm:p-5 mb-5">
            <div className="flex items-center gap-4">
              <div className="h-11 w-11 bg-sky-500 text-white font-black text-xl flex items-center justify-center border-2 border-sky-500 shrink-0">
                1
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-extrabold tracking-[0.22em] uppercase text-sky-700">
                  Step 1 &middot; Start here
                </div>
                <div className="font-display font-bold text-lg sm:text-xl text-slate-900 leading-tight mt-0.5">
                  Pick your material below to see live prices <span aria-hidden className="inline-block ml-1 animate-bounce">↓</span>
                </div>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto -mx-6 px-6 lg:mx-0 lg:px-0">
            <TabsList className="inline-flex h-auto bg-transparent border-2 border-slate-900 p-0 rounded-none divide-x-2 divide-slate-900 shadow-[0_10px_30px_-15px_rgba(14,165,233,0.5)]">
              {MATERIAL_ORDER.map((k, idx) => (
                <TabsTrigger
                  key={k}
                  value={k}
                  data-testid={`pricing-tab-${materialSlug(k)}`}
                  className={`group rounded-none px-4 sm:px-5 py-3 sm:py-4 text-xs font-bold uppercase tracking-[0.12em] data-[state=active]:bg-slate-900 data-[state=active]:text-white data-[state=active]:shadow-none text-slate-700 hover:bg-slate-100 ${idx === 0 ? "" : ""}`}
                >
                  <MaterialTab name={k} active={false} />
                </TabsTrigger>
              ))}
            </TabsList>
          </div>

          {/* Step 2 hint that appears right below the tabs */}
          <div className="mt-3 text-xs font-bold tracking-[0.18em] uppercase text-slate-500">
            Step 2 &middot; Pick a bin size below &middot; Step 3 &middot; Click a price to book
          </div>

          {MATERIAL_ORDER.map((k) => (
            <TabsContent key={k} value={k} className="mt-6">
              <div className="border-2 border-slate-900 bg-white">
                {PRICING[k].map((row, i) => (
                  <PriceRow key={i} row={row} material={k} />
                ))}
              </div>
              <div className="mt-5 text-xs text-slate-500 leading-relaxed">
                All quotes include 7 days rental, $20/day for additional days. Prices include specified tonnage.
                Going over? $0.15/kg, weighed on certified scale with receipt.{" "}
                <a href={`tel:${COMPANY.phoneRaw}`} data-testid="pricing-ask-us" className="text-sky-600 font-bold underline underline-offset-4">Ask us</a>.
              </div>
              {SINGLE_MATERIAL_LOADS.has(k) && (
                <div className="mt-8">
                  <CleanLoadGuarantee testid={`clean-load-guarantee-${materialSlug(k)}`} />
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>

        <div className="mt-10 grid lg:grid-cols-2 gap-5">
          <NoSurprisesGuarantee testid="no-surprises-guarantee-pricing" />
          <TextPhotoCta testid="text-photo-cta-pricing" />
        </div>
      </div>
    </section>
  );
}
